import React, { useCallback, useEffect, useMemo } from 'react';

import { SelectProps } from '../Select';

import SelectStore from '../hooks/useStore';

import { SelectOptionType } from '../types';
import { mergeOptions } from '../util';

export default function useCache<T extends SelectOptionType>(value: SelectProps['value'], parsedOptions: T[]) {
  const { cacheRef: _cacheRef } = SelectStore.useContainer();
  const cacheRef = _cacheRef as React.MutableRefObject<Map<string, T>>;

  const syncCacheOptionsMap = useCallback(() => {
    const prevCacheOptions = Array.from(cacheRef.current.values());

    if (Array.isArray(value)) {
      value.forEach((v) => {
        const mergedOptions = mergeOptions(prevCacheOptions, parsedOptions);

        const option = mergedOptions.find((option) => option.value === v);
        if (!option) return;

        cacheRef.current.set(v, option);
      });
    } else {
      const mergedOptions = mergeOptions(prevCacheOptions, parsedOptions);

      const option = mergedOptions.find((option) => option.value === value);
      if (!option) return;

      cacheRef.current.set(value, option);
    }
  }, [cacheRef, parsedOptions, value]);

  useEffect(() => {
    syncCacheOptionsMap();
  }, [syncCacheOptionsMap]);

  return useMemo(
    () => ({
      getCacheOption: (key: string) => cacheRef.current.get(key),
      getCacheOptions: () => Array.from(cacheRef.current.values()),
    }),
    [cacheRef],
  );
}
